import React, { useState, useContext } from "react";
import { Form, Row, Col, Button, Spinner } from "react-bootstrap";
import { AuthContext } from "../../contexts/AuthContext";
import { AddDisposalIntimation, addWorkflowEntry } from "../../services/disposal-intimation-service";
import { addLogDetails } from "../../services/logger-entry-service";
import { formatDateInDDMMYYYY, getCurrentDateTime } from "../../functions/common-functions";
import { IoArrowBack } from "react-icons/io5";
import { FaHistory } from "react-icons/fa";
import { getPresignedURL } from "../../functions/upload";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import successImage from "../../icons/success-tick-logo.svg";
import errorImage from "../../icons/error-tick-logo.svg";
import WorkflowHistoryModal from "./WorkflowHistoryModal";
import { getUsersForRole } from "../../services/user-service";

const DisposalIntimationApprove = (props) => {
  const MySwal = withReactContent(Swal);
  const context = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState("");
  const [remarks, setRemarks] = useState("");
  const [remarksError, setRemarksError] = useState("");
  const [showHistory, setShowHistory] = useState(false);

  const handleFileDownload = async (fieldName, fileName) => {
    try {
      const filePath = `DisposalIntimation/${props.rowData.IntimationNumber.replace(/\//g, '_')}/${fileName}`;
      const downloadURL = await getPresignedURL(filePath);

      if (downloadURL) {
        window.open(downloadURL, '_blank');
      } else {
        MySwal.fire({
          text: "Unable to download file",
          imageUrl: errorImage,
          customClass: "swal-success"
        });
      }
    } catch (error) {
      console.error("Error downloading file:", error);
      MySwal.fire({
        text: "Error downloading file",
        imageUrl: errorImage,
        customClass: "swal-success"
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!action) {
      MySwal.fire({
        text: "Please select an action",
        imageUrl: errorImage,
        customClass: "swal-success"
      });
      return;
    }

    // Validate remarks for Reject/Rework actions
    if ((action === "Reject" || action === "Rework") && !remarks.trim()) {
      setRemarksError(`Remarks are required for ${action} action`);
      return;
    }

    // Validate remarks pattern and length
    if (remarks.trim()) {
      const remarksPattern = /^[a-zA-Z0-9\s,.()]*$/;
      if (!remarksPattern.test(remarks)) {
        setRemarksError("Only alphanumeric characters, space, comma, dot and brackets are allowed");
        return;
      }
      if (remarks.length > 250) {
        setRemarksError("Remarks cannot exceed 250 characters");
        return;
      }
    }

    setRemarksError("");

    setLoading(true);
    try {
      let newStatus = "";
      let pendingWith = "";

      if (action === "Approve") {
        if (context.role === "DIVAPPROVER") {
          newStatus = "Pending for Final Approval";
          pendingWith = "FINAPPROVER,ADMIN";
        } else if (context.role === "FINAPPROVER" || context.role === "ADMIN") {
          newStatus = "Approved";
          pendingWith = "";
        }
      } else if (action === "Reject") {
        if (context.role === "DIVAPPROVER") {
          newStatus = "Rejected by Divisional Approval";
        } else if (context.role === "FINAPPROVER" || context.role === "ADMIN") {
          newStatus = "Rejected by Corporate Finance Approval";
        }
        pendingWith = "";
      } else if (action === "Rework") {
        newStatus = "Rework";
        pendingWith = props.rowData.CreatedByName;
      }
      let disposalData = props.rowData;
      disposalData.Status = newStatus;
      disposalData.PendingWith = pendingWith;

      await AddDisposalIntimation(disposalData);

      // Get current user names for pending roles
      let pendingWithNames = "";
      if (pendingWith) {
        try {

          const users = await getUsersForRole(pendingWith.split(",")[0]);
          const activeUsers = users.filter(user => user.DeleteIndication);

          if (pendingWith === "DIVAPPROVER") {
            const initiatorLocation = props.rowData.InitiatorLocation === "Other" ? props.rowData.IfOtherLoc : props.rowData.InitiatorLocation;
            const filteredUsers = activeUsers.filter(user => {
              const userLocations = user.Location ? user.Location.split(",").map(loc => loc.trim()) : [];
              return user.Division === props.rowData.InitiatorDivision &&
                userLocations.includes(initiatorLocation);
            });
            pendingWithNames = filteredUsers.map(user => user.UserName).join(", ");
          } else {
            pendingWithNames = activeUsers.map(user => user.UserName).join(", ");
          }
        } catch (error) {
          console.error("Error fetching pending users:", error);
          pendingWithNames = pendingWith;
        }
      }

      const workflowData = {
        Initiator: `${props.rowData.InitiatorUserID}@godrej.com`,
        Action: action,
        ActionBy: context.userId,
        ActionByName: context.userName,
        ActionDate: getCurrentDateTime("-"),
        Remarks: remarks || "",
        Status: newStatus,
        PendingWith: pendingWith,
        PendingWithNames: pendingWithNames
      };

      await addWorkflowEntry(props.rowData.IntimationNumber, workflowData);
      await addLogDetails(disposalData, action, props.rowData.IntimationNumber, "DisposalIntimationLog", { userName: context.userName, userId: context.userId }, "Transaction");

      setLoading(false);
      const actionText = action.toLowerCase().endsWith('e') ? action.toLowerCase().slice(0, -1) : action.toLowerCase();
      MySwal.fire({
        text: action === "Approve" && (context.role === "FINAPPROVER" || context.role === "ADMIN") ? `Disposal Intimation approved successfully` : `Request ${actionText}ed successfully`,
        imageUrl: successImage,
        customClass: "swal-success"
      });
      props.setReload(!props.reload);
      props.onHide();
    } catch (error) {
      console.error("Error updating status:", error);
      setLoading(false);
      MySwal.fire({
        text: "Failed to update request",
        imageUrl: errorImage,
        customClass: "swal-success"
      });
    }
  };

  return (
    <>
      {loading && <div className="spinner-box"> <Spinner animation="border" className="spinner" /></div>}

      <div className="back-navigation" onClick={props.onHide}>
        <IoArrowBack size={24} color="#810055" />
        <span>Back to Disposal Intimation Dashboard</span>
      </div>
      <div className="history-btn">
        <Button
          variant="outline-primary"
          className="semiBoldToggleButton cancelBtn"
          onClick={() => setShowHistory(true)}
          style={{ display: "flex", alignItems: "center", gap: "5px" }}
        >
          <FaHistory size={16} />
          History
        </Button>
      </div>

      <div className="disposal-form">
        <Form id="disposalDetails" name="disposalDetails" autoComplete="off">
          <div>
            <Row>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="IntimationNumber"
                    type="text"
                    name="IntimationNumber"
                    placeholder=" "
                    value={props.rowData?.IntimationNumber}
                    readOnly
                  />
                  <Form.Label htmlFor="IntimationNumber">Disposal Intimation Number</Form.Label>
                </Form.Group>
              </Col>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="Status"
                    type="text"
                    name="Status"
                    placeholder=" "
                    value={props.rowData?.Status}
                    readOnly
                  />
                  <Form.Label htmlFor="Status">Status</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="IntimationDate"
                    type="text"
                    name="IntimationDate"
                    placeholder=" "
                    value={formatDateInDDMMYYYY(props.rowData.IntimationDate)}
                    readOnly
                  />
                  <Form.Label htmlFor="IntimationDate">Date</Form.Label>
                </Form.Group>
              </Col>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="InitiatorUserID"
                    type="text"
                    name="InitiatorUserID"
                    placeholder=" "
                    value={props.rowData?.InitiatorUserID || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="InitiatorUserID">Initiator User ID</Form.Label>
                </Form.Group>
              </Col>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="InitiatorName"
                    type="text"
                    name="InitiatorName"
                    placeholder=" "
                    value={props.rowData?.InitiatorName || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="InitiatorName">Initiator Name</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="InitiatorContactNumber"
                    type="text"
                    name="InitiatorContactNumber"
                    placeholder=" "
                    value={props.rowData?.InitiatorContactNumber || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="InitiatorContactNumber">Initiator Contact Number</Form.Label>
                </Form.Group>
              </Col>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="InitiatorDivision"
                    type="text"
                    name="InitiatorDivision"
                    placeholder=" "
                    value={props.rowData?.InitiatorDivision || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="InitiatorDivision">Division</Form.Label>
                </Form.Group>
              </Col>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="LogisticCompany"
                    type="text"
                    name="LogisticCompany"
                    placeholder=" "
                    value={props.rowData?.LogisticCompany || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="LogisticCompany">Logistic Company</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="InitiatorLocation"
                    type="text"
                    name="InitiatorLocation"
                    placeholder=" "
                    value={props.rowData?.InitiatorLocation === "Other" && props.rowData?.IfOtherLoc
                      ? props.rowData.IfOtherLoc
                      : props.rowData?.InitiatorLocation || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="InitiatorLocation">Location</Form.Label>
                </Form.Group>
              </Col>
              <Col lg={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="AssetNonAssetItem"
                    type="text"
                    name="AssetNonAssetItem"
                    placeholder=" "
                    value={props.rowData?.AssetNonAssetItem || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="AssetNonAssetItem">Asset/Non-Asset Item</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="ItemCode"
                    type="text"
                    name="ItemCode"
                    placeholder=" "
                    value={props.rowData?.ItemCode || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="ItemCode">Item Code</Form.Label>
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="ItemDescription"
                    type="text"
                    name="ItemDescription"
                    placeholder=" "
                    value={props.rowData?.ItemDescription || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="ItemDescription">Item Description</Form.Label>
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="ItemType"
                    type="text"
                    name="ItemType"
                    placeholder=" "
                    value={props.rowData?.ItemType || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="ItemType">Item Type</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    id="ItemDetails"
                    name="ItemDetails"
                    placeholder=" "
                    value={props.rowData?.ItemDetails || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="ItemDetails">Item Details</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="ProductClass"
                    type="text"
                    name="ProductClass"
                    placeholder=" "
                    value={props.rowData?.ProductClass || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="ProductClass">Product Class</Form.Label>
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="CommodityCodes"
                    type="text"
                    name="CommodityCodes"
                    placeholder=" "
                    value={props.rowData?.CommodityCodes || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="CommodityCode">Commodity Code</Form.Label>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="Quantity"
                    type="text"
                    name="Quantity"
                    placeholder=" "
                    value={props.rowData?.Quantity || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="Quantity">Quantity</Form.Label>
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <Form.Control
                    id="UOM"
                    type="text"
                    name="UOM"
                    placeholder=" "
                    value={props.rowData?.UOM || ""}
                    readOnly
                  />
                  <Form.Label htmlFor="UOM">UOM</Form.Label>
                </Form.Group>
              </Col>
              <Col md={4} xs={12}>
                <Form.Group className="g-input">
                  <div className="d-flex align-items-center">
                    <Form.Control
                      id="GSTRate"
                      type="text"
                      name="GSTRate"
                      placeholder=" "
                      value={props.rowData?.GSTRate || ""}
                      readOnly
                    />
                    <Form.Label htmlFor="GSTRate">GST Rate</Form.Label>
                    <span style={{ marginLeft: "-3rem", fontSize: "21px", fontWeight: "600", color: "#707070" }}>%</span>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col xs={6} className="pb-3">
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="AttachImage">Attach Image</Form.Label>
                  {props.rowData?.AttachImage ? (
                    <div className="approval-value">
                      <span
                        className="file-link"
                        onClick={() => handleFileDownload('AttachImage', props.rowData.AttachImage)}
                      >
                        {props.rowData.AttachImage}
                      </span>
                    </div>
                  ) : (
                    <div className="approval-value">No file attached</div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={6} className="pb-3">
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="AttachDocument1">Attach Document 1</Form.Label>
                  {props.rowData?.AttachDocument1 ? (
                    <div className="approval-value">
                      <span
                        className="file-link"
                        onClick={() => handleFileDownload('AttachDocument1', props.rowData.AttachDocument1)}
                      >
                        {props.rowData.AttachDocument1}
                      </span>
                    </div>
                  ) : (
                    <div className="approval-value">No file attached</div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={6} className="pb-3">
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="AttachDocument2">Attach Document 2</Form.Label>
                  {props.rowData?.AttachDocument2 ? (
                    <div className="approval-value">
                      <span
                        className="file-link"
                        onClick={() => handleFileDownload('AttachDocument2', props.rowData.AttachDocument2)}
                      >
                        {props.rowData.AttachDocument2}
                      </span>
                    </div>
                  ) : (
                    <div className="approval-value">No file attached</div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={6} className="pb-3">
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="AttachDocument3">Attach Document 3</Form.Label>
                  {props.rowData?.AttachDocument3 ? (
                    <div className="approval-value">
                      <span
                        className="file-link"
                        onClick={() => handleFileDownload('AttachDocument3', props.rowData.AttachDocument3)}
                      >
                        {props.rowData.AttachDocument3}
                      </span>
                    </div>
                  ) : (
                    <div className="approval-value">No file attached</div>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {props.action === "Approval" && (
              <>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label className="approval-label">
                        Action <span className="req-input">*</span>
                      </Form.Label>
                      <div className="approval-actions">
                        <Form.Check
                          type="radio"
                          id="approve"
                          name="action"
                          label="Approve"
                          value="Approve"
                          checked={action === "Approve"}
                          onChange={(e) => setAction(e.target.value)}
                        />
                        <Form.Check
                          type="radio"
                          id="rework"
                          name="action"
                          label="Rework"
                          value="Rework"
                          checked={action === "Rework"}
                          onChange={(e) => setAction(e.target.value)}
                        />
                        <Form.Check
                          type="radio"
                          id="reject"
                          name="action"
                          label="Reject"
                          value="Reject"
                          checked={action === "Reject"}
                          onChange={(e) => setAction(e.target.value)}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="g-input">
                      <Form.Control
                        as="textarea"
                        rows={2}
                        id="remarks"
                        name="remarks"
                        placeholder=" "
                        value={remarks}
                        maxLength={250}
                        className={remarksError ? "red-border" : ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          const remarksPattern = /^[a-zA-Z0-9\s,.()]*$/;

                          if (value === "" || remarksPattern.test(value)) {
                            setRemarks(value);
                            setRemarksError("");
                          }
                        }}
                      />
                      <Form.Label htmlFor="remarks">
                        Remarks {(action === "Reject" || action === "Rework") && <span className="req-input">*</span>}
                      </Form.Label>
                      {remarksError && (
                        <Form.Text className="text-danger">
                          {remarksError}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </div>
          {props.action === "Approval" && (
            <Row className="disposal-footer">
              <Col xs={6} md={6}>
                <Button className="semiBoldToggleButton cancelBtn" variant="outline-success" onClick={props.onHide}>
                  Cancel
                </Button>
              </Col>
              <Col xs={6} md={6}>
                <Button
                  variant="success"
                  className="btn-add-color semiBoldToggleButton rightAlignedButton"
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "Submit"}
                </Button>
              </Col>
            </Row>
          )}
        </Form>
      </div>

      <WorkflowHistoryModal
        show={showHistory}
        onHide={() => setShowHistory(false)}
        intimationNumber={props.rowData?.IntimationNumber}
      />
    </>
  );
};

export default DisposalIntimationApprove;